import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom-v5-compat';
import { Dispatch, bindActionCreators } from 'redux';
import { ButtonGroup, Link } from 'src/components/atoms';
import { isRequiredFeatureEnabled } from 'src/components/auth';
import { handleErrorToast } from 'src/components/molecules';
import { useGetPublicFeaturesQuery } from 'src/graphql';
import { useOpenReportingLocation } from 'src/hooks';
import { PhoneNumberLoginState } from 'src/reducers/OpenReporting/VaultTalkLoginValues.reducer';
import * as OpenReportingCreators from '../../../creators/OpenReporting.creators';
import * as Types from '../../../types';
import OPRLandingPageButtons from '../OPRLandingPage/OPRLandingPageButtons';
import { OpenReportingContainer } from '../OpenReportingContainer/OpenReportingContainer';
import { ButtonGroupWrapper, LinkWrapper } from './OPRLoginPage.styles';
import PhoneNumberAndPinInput from './PhoneNumberAndPinInput';
import UserAndPasswordInput from './UserAndPasswordInput';

interface Props {
  organisation: string;
  logo: string;
  loginValuesVaultTalk?: PhoneNumberLoginState;
  loginValues?: {
    username: string;
    password: string;
  };
  createVaultTalkPublicSession(
    phoneNumber: string,
    pin: string,
    slug: string,
  ): void;
  createPublicSession(username: string, password: string, slug: string): void;
}

export enum i18nLoginOptions {
  VAULT_ID = 'public.CreatePublicReport.vaultId',
  VAULT_TALK = 'public.CreatePublicReport.vaultTalk',
}

export enum RedirectOptions {
  RELATIONSHIP = 'relationship',
  REPORTS = 'reports',
  LOGIN = 'login',
}

const OPRLoginPage = (props: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loginIsDisabled, setLoginIsDisabled] = useState(false);
  const { page } = useOpenReportingLocation();

  const { data: publicFeatures } = useGetPublicFeaturesQuery({
    slug: props.organisation,
  });

  const isVaultTalkEnabled = isRequiredFeatureEnabled(
    (publicFeatures?.getPublicFeatures as Types.Feature[]) ?? [],
    Types.FeatureFlag.VAULT_TALK,
  );

  const isOPREnabled = isRequiredFeatureEnabled(
    (publicFeatures?.getPublicFeatures as Types.Feature[]) ?? [],
    Types.FeatureFlag.PUBLIC_REPORTING,
  );

  const isOPRPasswordResetEnabled = isRequiredFeatureEnabled(
    (publicFeatures?.getPublicFeatures as Types.Feature[]) ?? [],
    Types.FeatureFlag.OPR_PASSWORD_RESET,
  );

  const [loginOption, setLoginOption] = useState(t(i18nLoginOptions.VAULT_ID));

  const showMethodToggle = isOPREnabled && isVaultTalkEnabled;

  const isVaultIdLogin = showMethodToggle
    ? loginOption === t(i18nLoginOptions.VAULT_ID)
    : isOPREnabled;

  useEffect(() => {
    isVaultIdLogin
      ? setLoginIsDisabled(
          props.loginValues?.username === '' ||
            props.loginValues?.password === '',
        )
      : setLoginIsDisabled(
          props.loginValuesVaultTalk?.phoneNumber === '' ||
            props.loginValuesVaultTalk?.pin === '',
        );
  }, [isVaultIdLogin, props.loginValuesVaultTalk, props.loginValues]);

  const toastMessage = isVaultIdLogin
    ? t('public.CreatePublicReport.usernameLoginFailed')
    : t('public.CreatePublicReport.phoneNumberLoginFailed');

  if (!props.organisation) return null;

  const onLogin = async () => {
    try {
      const {
        organisation,
        loginValuesVaultTalk,
        loginValues,
        createPublicSession,
        createVaultTalkPublicSession,
      } = props;
      if (isVaultIdLogin && loginValues) {
        const { username, password } = loginValues;
        await createPublicSession(username, password, organisation);
      } else {
        if (loginValuesVaultTalk) {
          const { phoneNumber, pin } = loginValuesVaultTalk;
          await createVaultTalkPublicSession(phoneNumber, pin, organisation);
        }
      }
      navigate(`/${props.organisation}/open-reporting/reports`);
    } catch (errors: unknown) {
      const errorsTyped = errors as Error[];
      if (
        errorsTyped[0]?.message &&
        !errorsTyped[0]?.message.includes('Please check your credentials')
      ) {
        handleErrorToast({ toastMessage: errorsTyped[0].message });

        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      } else {
        handleErrorToast({
          toastMessage: toastMessage,
        });

        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }
      throw errors;
    }
  };
  return (
    <OpenReportingContainer organisation={props.organisation} logo={props.logo}>
      {showMethodToggle && (
        <ButtonGroupWrapper>
          <ButtonGroup
            options={[
              t(i18nLoginOptions.VAULT_ID),
              t(i18nLoginOptions.VAULT_TALK),
            ]}
            selectedOption={loginOption}
            size="medium"
            setSelectedOption={setLoginOption}
            buttonWidth="140px"
            data-testid="opr-button-group"
            buttonGroupOptionTestId={'opr-login-option'}
          />
        </ButtonGroupWrapper>
      )}
      {isVaultIdLogin ? (
        <>
          <UserAndPasswordInput handleLogin={onLogin} />
          {isOPRPasswordResetEnabled && (
            <Link to={`/${props.organisation}/open-reporting/reset`}>
              {t('reporting.ReportingLogin.forgotPassword')}
            </Link>
          )}
        </>
      ) : (
        <PhoneNumberAndPinInput handleLogin={onLogin} />
      )}
      <LinkWrapper>
        <Link
          size="small"
          target="_blank"
          href="https://intercom.help/vault-platform-384e32b6cea0/en/"
        >
          {t('public.CreatePublicReport.helpPage')}
        </Link>
      </LinkWrapper>
      <OPRLandingPageButtons
        oprPage={page}
        handleOnLogin={onLogin}
        disabled={loginIsDisabled}
        organisation={props.organisation}
      />
    </OpenReportingContainer>
  );
};

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      ...OpenReportingCreators,
    },
    dispatch,
  );

const mapStateToProps = (state: Types.State) => ({
  organisation: state.openReporting?.openReportReducer?.organisation ?? '',
  logo: state?.openReporting?.openReportReducer?.logo ?? '',
  features: state.features,
  loginValuesVaultTalk: state.openReporting.phoneNumberLoginValues,
  loginValues: state.openReporting.loginReducer,
});
export default connect(mapStateToProps, mapDispatchToProps)(OPRLoginPage);
